import React, { useState, useEffect, useCallback } from 'react';
import { Home, Building, Users, DollarSign, ListPlus, Edit3, Trash2, PlusCircle, ChevronDown, ChevronRight, Settings, FileText, AlertTriangle, CreditCard, Eye, ListChecks, Columns, LogOut } from 'lucide-react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
import LoginView from './components/LoginView';
import DashboardView from './components/DashboardView';
import BuildingManagementView from './components/BuildingManagementView';
import { Toast, Modal, ConfirmationModal, NavItem } from './components/Helpers';
import BlockManagementView from './components/BlockManagementView';
import ApartmentManagementView from './components/ApartmentManagementView';
import PeopleManagementView from './components/PeopleManagementView';
import DuesTemplateView from './components/DuesTemplateView';
import DuesDefinitionView from './components/DuesDefinitionView';
import CommonExpensesView from './components/CommonExpensesView';
import MeterReadingsView from './components/MeterReadingsView';
import ExemptionRulesView from './components/ExemptionRulesView';
import GenerateDuesView from './components/GenerateDuesView';
import DuesReportView from './components/DuesReportView';
import DebtorReportView from './components/DebtorReportView'; // Import DebtorReportView
import { fetchBuildings, addBuilding, updateBuilding, deleteBuilding } from './api/buildingApi';
import { fetchBlocks, addBlock, updateBlock, deleteBlock } from './api/blockApi';
import { fetchApartments, addApartment, updateApartment, deleteApartment } from './api/apartmentApi';
import { fetchPeople, addPerson, updatePerson, deletePerson } from './api/personApi';
import { fetchDuesDefinitions, addDuesDefinition, updateDuesDefinition, deleteDuesDefinition } from './api/duesDefinitionApi';
import { fetchPeriodicalDuesSummaries, generateAllDuesForPeriod, recordPayment, fetchPaymentHistory, updatePaymentHistory, deletePaymentHistory } from './api/periodicalDuesApi';
import LandingPage from './LandingPage';

ChartJS.register(ArcElement, Tooltip, Legend, Title);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Helper function to get JWT token from localStorage
const getToken = () => localStorage.getItem('token');

// Generic API fetcher with authentication
const authenticatedFetch = async (url, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, { ...options, headers });

  if (response.status === 401 || response.status === 403) {
    localStorage.removeItem('token');
    localStorage.removeItem('currentUser');
    window.location.reload();
    throw new Error('Oturum süresi doldu veya yetkiniz yok. Lütfen tekrar giriş yapın.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Ağ yanıtı başarısız oldu.' }));
    throw new Error(errorData.message || 'Bir hata oluştu.');
  }
  return response;
};

// Login
const loginAPI = async (credentials) => {
  const response = await fetch(`${API_BASE_URL}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }, // Corrected Content-Type
    body: JSON.stringify(credentials),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Giriş başarısız oldu.' }));
    throw new Error(errorData.message || 'Giriş yapılamadı');
  }
  return response.json();
};

// Dashboard Summary
const fetchDashboardSummaryAPI = async (period) => {
  let url = `${API_BASE_URL}/dashboard/summary`;
  if (period) {
    url += `?period=${period}`;
  }
  const response = await authenticatedFetch(url);
  return response.json();
};

// --- Ana App Component'i ---
const App = () => {
  const [currentView, setCurrentView] = useState('landing'); // Başlangıçta landing view’ını göster
  const [toast, setToast] = useState({ message: '', type: '' });
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const showToast = useCallback((message, type = 'success', duration = 4000) => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: '', type: '' }), duration);
  }, []);

  const checkAuth = useCallback(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('currentUser');
    if (token && storedUser) {
      try {
        setCurrentUser(JSON.parse(storedUser));
        setIsAuthenticated(true);
        setCurrentView('dashboard'); // Giriş yapılmışsa dashboard’a yönlendir
      } catch (e) {
        console.error("Stored user data is corrupted", e);
        localStorage.removeItem('token');
        localStorage.removeItem('currentUser');
        setIsAuthenticated(false);
        setCurrentUser(null);
        setCurrentView('landing'); // Hata varsa landing page’e dön
      }
    } else {
      setIsAuthenticated(false);
      setCurrentUser(null);
      setCurrentView('landing'); // Giriş yapılmamışsa landing page göster
    }
  }, []);

  useEffect(() => {
    checkAuth();
    // Removed print-related useEffect logic
  }, [checkAuth]);

  const handleLoginSuccess = () => {
    checkAuth();
    setCurrentView('dashboard'); // Giriş başarılıysa dashboard’a yönlendir
  };

  const handleGoToLogin = () => {
    setCurrentView('login'); // Giriş ekranına yönlendir
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('currentUser');
    setIsAuthenticated(false);
    setCurrentUser(null);
    setCurrentView('landing'); // Çıkış yapıldığında landing page’e dön
    showToast('Başarıyla çıkış yapıldı.', 'success');
  };

  const renderView = () => {
    switch (currentView) {
      case 'landing':
        return <LandingPage onGoToLogin={handleGoToLogin} />;
      case 'login':
        return <LoginView onLoginSuccess={handleLoginSuccess} showToast={showToast} />;
      case 'dashboard':
        return <DashboardView showToast={showToast} currentUser={currentUser} />;
      case 'buildings':
        return <BuildingManagementView showToast={showToast} currentUser={currentUser} />;
      case 'blocks':
        return <BlockManagementView showToast={showToast} currentUser={currentUser} />;
      case 'apartments':
        return <ApartmentManagementView showToast={showToast} currentUser={currentUser} />;
      case 'people':
        return <PeopleManagementView showToast={showToast} currentUser={currentUser} />;
      case 'duesTemplates':
        return <DuesTemplateView showToast={showToast} />;
      case 'duesDefinitions':
        return <DuesDefinitionView showToast={showToast} currentUser={currentUser} />;
      case 'commonExpenses':
        return <CommonExpensesView showToast={showToast} currentUser={currentUser} />;
      case 'generateDues':
        return <GenerateDuesView showToast={showToast} currentUser={currentUser} setCurrentView={setCurrentView} />; // Pass setCurrentView
      case 'duesReport':
        return <DuesReportView showToast={showToast} currentUser={currentUser} />;
      case 'debtorReport':
        return <DebtorReportView showToast={showToast} currentUser={currentUser} />; // Render DebtorReportView
      case 'meterReadings':
        return <MeterReadingsView showToast={showToast} currentUser={currentUser} />;
      case 'exemptionRules':
        return <ExemptionRulesView showToast={showToast} currentUser={currentUser} />;
      default:
        return <LandingPage onGoToLogin={handleGoToLogin} />;
    }
  };

  return (
    <div className="flex h-screen bg-gray-100 font-sans">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: '' })} />
      {isAuthenticated ? (
        <>
          <aside className="w-72 bg-sky-800 text-white p-4 space-y-4 overflow-y-auto shadow-lg">
            <div className="flex justify-center pt-2 pb-4 bg-white rounded-md shadow-sm">
              <img
                src={process.env.PUBLIC_URL + '/monapart.jpg'}
                alt="MonApart Logo"
                className="h-48 w-auto"
              />
            </div>
            <p className="text-xs text-center text-sky-300 border-t border-sky-700 pt-3">
              Kullanıcı: {currentUser?.name || 'Misafir'}
            </p>
            <nav className="pt-2">
              <ul>
                <NavItem
                  icon={<Home size={20} />}
                  label="Gösterge Paneli"
                  viewName="dashboard"
                  currentView={currentView}
                  setCurrentView={setCurrentView}
                />
                <NavItem
                  icon={<Building size={20} />}
                  label="Bina ve Blok Yönetimi"
                  viewName="buildings"
                  currentView={currentView}
                  setCurrentView={setCurrentView}
                  subItems={[
                    { icon: <Building size={18} />, label: 'Binalar', viewName: 'buildings' },
                    { icon: <Columns size={18} />, label: 'Bloklar', viewName: 'blocks' },
                  ]}
                />
                <NavItem
                  icon={<Home size={20} />}
                  label="Daire Yönetimi"
                  viewName="apartments"
                  currentView={currentView}
                  setCurrentView={setCurrentView}
                />
                <NavItem
                  icon={<Users size={20} />}
                  label="Kişi Yönetimi"
                  viewName="people"
                  currentView={currentView}
                  setCurrentView={setCurrentView}
                />
                <NavItem
                  icon={<Settings size={20} />}
                  label="Aidat & Gider Ayarları"
                  viewName="settings"
                  currentView={currentView}
                  setCurrentView={setCurrentView}
                  subItems={[
                    { icon: <Columns size={18} />, label: 'Aidat Şablonları', viewName: 'duesTemplates' },
                    { icon: <DollarSign size={18} />, label: 'Dönemsel Aidatlar', viewName: 'duesDefinitions' },
                    { icon: <ListPlus size={18} />, label: 'Ortak Gider Tanımları', viewName: 'commonExpenses' },
                    { icon: <Eye size={18} />, label: 'Sayaç Okumaları', viewName: 'meterReadings' },
                    { icon: <ListChecks size={18} />, label: 'Muafiyet Kuralları', viewName: 'exemptionRules' },
                  ]}
                />
                <NavItem
                  icon={<FileText size={20} />}
                  label="Aidat Oluştur & Takip"
                  viewName="generateDues"
                  currentView={currentView}
                  setCurrentView={setCurrentView}
                />
                <NavItem
                  icon={<FileText size={20} />}
                  label="Aidat Raporu"
                  viewName="duesReport"
                  currentView={currentView}
                  setCurrentView={setCurrentView}
                />
                <NavItem
                  icon={<CreditCard size={20} />}
                  label="Borçlu Raporu"
                  viewName="debtorReport"
                  currentView={currentView}
                  setCurrentView={setCurrentView}
                />
                <li className="mb-1 mt-4 border-t border-sky-700 pt-4">
                  <button
                    onClick={handleLogout}
                    className="flex items-center p-3 rounded-lg hover:bg-sky-600 transition-colors w-full text-left"
                  >
                    <LogOut size={20} />
                    <span className="ml-3">Çıkış Yap</span>
                  </button>
                </li>
              </ul>
            </nav>
          </aside>
          <main className={`flex-1 p-6 overflow-y-auto`}>{renderView()}</main>
        </>
      ) : (
        renderView()
      )}
    </div>
  );
};

export default App;
